import { Box, Button as MuiButton, Card, Chip, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import {
  Confirm,
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  TextInput,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import { Iconify } from 'src/components/iconify';
import { SplashScreen } from 'src/components/loading-screen';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { resetTargetProxyScore } from '../../../services/proxyManagerService';
import { Resources } from '../../resource';
import { QUARANTINED_LEVELS } from '../constants';
import { resetProxyScoresCursorStore } from '../dataProvider';
import { HealthCheckButton } from '../HealthCheckButton';
import { ResetQuarantineButton } from '../ResetQuarantineButton';

const PERMISSION_RESOURCE = 'proxy-manager';
const RESOURCE = Resources.proxyManagerProxyScores;

const ScoreBar = ({ score }: { score: number }) => {
  const pct = Math.round(score * 100);
  const color = score === 0 ? '#ef4444' : score < 0.5 ? '#facc15' : '#22c55e';
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 80,
          height: 8,
          borderRadius: 1,
          bgcolor: 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 1 }} />
      </Box>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 38, color }}>
        {score.toFixed(3)}
      </Typography>
    </Stack>
  );
};

const ListLoadingOverlay = () => {
  const { isLoading } = useListContext();
  if (!isLoading) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        bgcolor: 'rgba(255,255,255,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SplashScreen />
    </Box>
  );
};

const ProxyScoreListActions = () => {
  const { filterValues, setFilters, setPage } = useListContext();
  const translate = useTranslate();
  const hasActiveFilters = Object.keys(filterValues ?? {}).length > 0;

  const handleReset = () => {
    resetProxyScoresCursorStore();
    setPage(1);
    setFilters({}, []);
  };

  return (
    <TopToolbar sx={{ alignItems: 'center', p: 2, width: '100%' }}>
      <FilterForm
        filters={[
          <TextInput
            key="target"
            source="target"
            label="module.proxyManager.proxyScore.target"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 300 }}
          />,
        ]}
      />
      <MuiButton variant="outlined" size="medium" onClick={handleReset} disabled={!hasActiveFilters}>
        {translate('common.reset', { _: 'Reset' })}
      </MuiButton>
    </TopToolbar>
  );
};

const ResetScoreButton = ({ record }: { record: any }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const proxyId = record?.proxy?.id;
  const targetKey = record?.targetKey;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await resetTargetProxyScore(String(proxyId), String(targetKey));
      notify(translate('module.proxyManager.proxyScore.resetSuccess'), {
        type: 'success',
        autoHideDuration: 1500,
      });
      resetProxyScoresCursorStore();
      refresh();
    } catch (error: any) {
      notify(error?.message || translate('module.proxyManager.unknownError'), { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <MuiButton
        variant="outlined"
        color="error"
        size="small"
        disabled={!proxyId || !targetKey}
        startIcon={<Iconify icon="solar:restart-bold" />}
        onClick={() => setOpen(true)}
        sx={{ minWidth: 120}}
      >
        {translate('module.proxyManager.proxyScore.reset')}
      </MuiButton>
      <Confirm
        isOpen={open}
        loading={loading}
        title={translate('module.proxyManager.proxyScore.reset')}
        content={translate('module.proxyManager.proxyScore.resetConfirm', {
          address: `${record?.proxy?.host}:${record?.proxy?.port}`,
          target: targetKey,
        })}
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const ProxyScoreResetQuarantineButton = ({ record }: { record: any }) => {
  const refresh = useRefresh();

  return (
    <ResetQuarantineButton
      proxyId={record?.proxy?.id}
      address={`${record?.proxy?.host}:${record?.proxy?.port}`}
      quarantineLevel={record?.proxy?.quarantineLevel}
      onSuccess={() => {
        resetProxyScoresCursorStore();
        refresh();
      }}
    />
  );
};

const ProxyScoreHealthCheckButton = ({ record }: { record: any }) => {
  const refresh = useRefresh();
  const targetKey = record?.targetKey;

  return (
    <HealthCheckButton
      proxyId={record?.proxy?.id}
      address={`${record?.proxy?.host}:${record?.proxy?.port}`}
      defaultTargets={targetKey ? [targetKey] : []}
      protocol={record?.proxy?.protocol}
      onSuccess={() => {
        resetProxyScoresCursorStore();
        refresh();
      }}
    />
  );
};

const ProxyScorePagination = () => (
  <Box display="flex" alignItems="center" justifyContent="flex-end" px={2} py={1}>
    <Pagination rowsPerPageOptions={[20, 50, 100, 200]} />
  </Box>
);

export const ProxyScorePanel = () => (
  <Card sx={{ mt: 2, pb: 2 }}>
    <List
      resource={RESOURCE}
      actions={<ProxyScoreListActions />}
      empty={false}
      perPage={20}
      exporter={false}
      pagination={<ProxyScorePagination />}
    >
      <Box sx={{ overflow: 'auto', position: 'relative' }}>
        <ListLoadingOverlay />
        <Datagrid
          rowClick={false}
          empty={<EmptyContent />}
          bulkActionButtons={false}
          sx={{ '& .RaDatagrid-tableWrapper': { overflowX: 'auto' } }}
        >
          <FunctionField
            label="module.proxyManager.proxyScore.targetKey"
            sortable={false}
            render={(record: any) => (
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', minWidth: 300, wordBreak: 'break-all' }}
              >
                {record.targetKey}
              </Typography>
            )}
          />
          <FunctionField
            label="module.proxyManager.proxyScore.score"
            sortable={false}
            render={(record: any) => <ScoreBar score={record.score} />}
          />
          <FunctionField
            label="module.proxyManager.address"
            sortable={false}
            render={(record: any) => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                {record.proxy?.host}:{record.proxy?.port}
              </Typography>
            )}
          />
          <FunctionField
            label="module.proxyManager.protocol"
            sortable={false}
            render={(record: any) => record.proxy?.protocol || '-'}
          />
          <FunctionField
            label="module.proxyManager.tier"
            sortable={false}
            render={(record: any) => (
              <Chip label={record.proxy?.tier || '-'} size="small" variant="outlined" />
            )}
          />
          <FunctionField
            label="module.proxyManager.geo"
            sortable={false}
            render={(record: any) => {
              const geo = record.proxy?.geo;
              if (!geo) return '-';
              return (
                [geo.countryCode, geo.city, geo.region].filter(Boolean).join(' / ') || '-'
              );
            }}
          />
          <FunctionField
            label="module.proxyManager.ispName"
            sortable={false}
            render={(record: any) => record.proxy?.isp?.name || '-'}
          />
          <FunctionField
            label="module.proxyManager.source"
            sortable={false}
            render={(record: any) => record.proxy?.source || '-'}
          />
          <FunctionField
            label="module.proxyManager.quarantineLevel"
            sortable={false}
            render={(record: any) => {
              const ql = record.proxy?.quarantineLevel;
              if (!ql) return '-';
              const isRed = QUARANTINED_LEVELS.includes(ql);
              return (
                <Typography variant="body2" sx={{ color: isRed ? 'error.main' : 'success.main', fontWeight: 500, minWidth: 120, textTransform: 'capitalize' }} noWrap>
                  {ql}
                </Typography>
              );
            }}
          />
          <FunctionField
            label="module.proxyManager.tags"
            sortable={false}
            render={(record: any) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(record.proxy?.tags ?? []).length
                  ? record.proxy.tags.map((tag: string) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))
                  : '-'}
              </Stack>
            )}
          />
          <FunctionField
            label="common.actions"
            textAlign="center"
            sortable={false}
            render={(record: any) => (
              <Stack direction="column" spacing={1} justifyContent="flex-end">
                <PermissionGuard resource={PERMISSION_RESOURCE} action="update" fallback={<></>}>
                  <ResetScoreButton record={record} />
                </PermissionGuard>
                <ProxyScoreResetQuarantineButton record={record} />
                <ProxyScoreHealthCheckButton record={record} />
              </Stack>
            )}
          />
        </Datagrid>
      </Box>
    </List>
  </Card>
);

export default ProxyScorePanel;
